<script lang="ts" setup>
import { StepsController } from '@/controllers/StepsController.ts'
import SectionHeadline from '@/components/SectionHeadline.vue'
import ExclusionHint from '@/components/steps/components/ExclusionHint.vue'
import FormElement from '@/components/FormElement.vue'
import { onMounted, ref, watch } from 'vue'
import { BufferStrategy, CharacteristicType } from '@/helper/Types.ts'
import formStore from '@/store/FormStore.ts'
import RadioButton from '@/components/RadioButton.vue'
import projectStore from '@/store/ProjectStore.ts'
import IconDecouplingBuffer from '@/components/icons/IconDecouplingBuffer.vue'
import IconReturnFlowBuffer from '@/components/icons/IconReturnFlowBuffer.vue'
import { api } from '@/services/ApiService.ts'
import { ProductResponse } from '@/services/models/ProductResponse.ts'
import { BufferTankCharacteristic } from '@/services/models/BufferTankCharacteristic.ts'
import { HydraulicTowerCharacteristic } from '@/services/models/HydraulicTowerCharacteristic.ts'
import { MountingMaterial } from '@/models/MountingMaterial.ts'
import MountingMaterialItem from '@/components/steps/installation/MountingMaterialItem.vue'
import { Product } from '@/services/models/implementations/Product.ts'
import { MountingCharacteristic } from '@/services/models/MountingCharacteristic.ts'
import IconHybridKit from '@/components/icons/IconHybridKit.vue'
import IconHeatExchanger from '@/components/icons/IconHeatExchanger.vue'
import userStore from '@/store/UserStore.ts'
import InfoBadge from '@/components/badges/InfoBadge.vue'

const heatExchanger = ref<boolean>(formStore.data.heatExchanger as boolean)
const hybridKit = ref<boolean>(formStore.data.hybridKit as boolean)
const bufferStrategy = ref<BufferStrategy | undefined>(formStore.data.bufferStrategy as BufferStrategy)
const mountingMaterial = ref<MountingMaterial | undefined>(formStore.data.mountingMaterial as MountingMaterial)
const combinableHeatExchangers = ref<ProductResponse[]>([])
const combinableHeatExchangersSalePackage = ref<ProductResponse | undefined>(undefined)
const combinableHybridKit = ref<ProductResponse[]>([])
const availableMountingMaterials = ref<MountingMaterial[]>([])
const isDecouplingBufferStrategyDisabled = ref<boolean>(false)
const isReturnFlowBufferStrategyDisabled = ref<boolean>(false)

watch(heatExchanger, h => formStore.data.heatExchanger = h)
watch(hybridKit, h => formStore.data.hybridKit = h)

watch(bufferStrategy, b => formStore.data.bufferStrategy = b)
watch(mountingMaterial, b => formStore.data.mountingMaterial = b)

const projectHasBufferTank = ref(!!projectStore.projectProperties.bufferTankId.val || !!projectStore.projectProperties.coolingBufferTankId.val)

onMounted(async () => {
  const combinableHeatExchanger = await api.loadCombinables(CharacteristicType.HEAT_EXCHANGER)
  const combinableHeatExchangersInSalePackage = combinableHeatExchanger.find(heatExchanger => heatExchanger.inSalePackage)
  combinableHeatExchangers.value = combinableHeatExchanger

  if (combinableHeatExchangersInSalePackage) {
    combinableHeatExchangersSalePackage.value = combinableHeatExchangersInSalePackage
    heatExchanger.value = true
  }

  combinableHybridKit.value = await api.loadHybridKit()

  const mountingMaterials = await api.loadCombinables(CharacteristicType.MOUNTING)
  const mountingMaterialsInSalePackage = mountingMaterials.find(mountingMaterial => mountingMaterial.inSalePackage)

  availableMountingMaterials.value = await getAvailableMountingMaterials(mountingMaterials)

  if (mountingMaterialsInSalePackage) {
    const material = mountingMaterials
      .map(m => new Product(m))
      .filter(p => p.inSalePackage)
      .map(p => p.getProductCharacteristic<MountingCharacteristic>(CharacteristicType.MOUNTING))
      .map(m => m.material)

    mountingMaterial.value = material[0]
  }

  if (projectHasBufferTank.value) {
    setBufferTankStrategy()
  }
})

async function getAvailableMountingMaterials(mountings: ProductResponse[]): Promise<MountingMaterial[]> {

  return mountings
    .map(m => new Product(m))
    .map(p => p.getProductCharacteristic<MountingCharacteristic>(CharacteristicType.MOUNTING))
    .map(m => m.material)
}

function setBufferTankStrategy(): void {
  const bufferTanks = [
    ...projectStore.getArticlesByCharacteristic(CharacteristicType.COOLING_BUFFER_TANK).map(b => b.details?.getProductCharacteristic<BufferTankCharacteristic>(CharacteristicType.COOLING_BUFFER_TANK)),
    ...projectStore.getArticlesByCharacteristic(CharacteristicType.BUFFER_TANK).map(b => b.details?.getProductCharacteristic<BufferTankCharacteristic>(CharacteristicType.BUFFER_TANK))
  ]

  const bufferStrategyReturnFlowOnly = bufferTanks.every(b => b?.bufferTypes && b.bufferTypes.every(s => s === BufferStrategy.RETURN_FLOW))

  const hydraulicTower = projectStore.getArticleByCharacteristic(CharacteristicType.HYDRAULIC_TOWER)?.details
    ?.getProductCharacteristic<HydraulicTowerCharacteristic>(CharacteristicType.HYDRAULIC_TOWER)

  isReturnFlowBufferStrategyDisabled.value = (projectStore.hasMixedCircuit() && !hydraulicTower?.hasBuiltInDistributor) || ((projectStore.projectProperties.circuits.val?.length || 0) > 2)
  isDecouplingBufferStrategyDisabled.value = bufferStrategyReturnFlowOnly || !!hydraulicTower?.hasBuiltInDistributor

  if (isReturnFlowBufferStrategyDisabled.value) {
    bufferStrategy.value = BufferStrategy.DECOUPLING
  } else if (isDecouplingBufferStrategyDisabled.value) {
    bufferStrategy.value = BufferStrategy.RETURN_FLOW
  }
}

function validate(): boolean {
  return true
}

defineExpose({
  validate
})
</script>

<template>
  <div class="steps__content" data-hj-whitelist>
    <form id="installation-form" class="form steps__form card" @submit.prevent="StepsController.nextClicked()">
      <section>
        <div class="section__headline">
          <SectionHeadline translation-key="step_installation.section_hydraulic_functions_headline" />
          <p>{{ $t('step_installation.section_hydraulic_functions_description') }}</p>
        </div>

        <div class="fTable is--products-table has--selects is--add-product-table">
          <div class="fTable__item is--select">
            <FormElement>
              <label class="form__checkbox">
                <input
                  id="heatExchanger"
                  v-model="heatExchanger"
                  :disabled="!combinableHeatExchangers.length || !!combinableHeatExchangersSalePackage"
                  type="checkbox"
                >
                <span class="form__cm" />
              </label>

              <label class="fTable__tr fTable__cb" for="heatExchanger">
                <div class="fTable__td column is--product">
                  <IconHeatExchanger />
                  <div class="fTable__product-infos">
                    <h3 class="product__title">{{ $t('step_installation.heat_exchanger_title') }}</h3>
                    <p>{{ $t('step_installation.heat_exchanger_description') }}</p>
                    <InfoBadge :info-text="$t('step_installation.heat_exchanger_info')" />
                  </div>
                </div>
              </label>
            </FormElement>
          </div>

          <div
            v-if="combinableHybridKit.length"
            class="fTable__item is--select"
          >
            <FormElement>
              <label class="form__checkbox">
                <input
                  id="hybridKit"
                  v-model="hybridKit"
                  type="checkbox"
                >
                <span class="form__cm" />
              </label>

              <label class="fTable__tr fTable__cb" for="hybridKit">
                <div class="fTable__td column is--product">
                  <IconHybridKit />
                  <div class="fTable__product-infos">
                    <h3 class="product__title">{{ $t('step_installation.hybrid_kit_title') }}</h3>
                    <p v-sanitize-html="$t('step_installation.hybrid_kit_description')" />
                    <a
                      v-if="userStore.hybridKitInfoLink"
                      :href="userStore.hybridKitInfoLink"
                      target="_blank"
                      class="external-link"
                      data-testid="hybrid-kit-link"
                      @click.stop
                    >
                      {{ $t('step_installation.hybrid_kit_link') }}
                      <i class="ic-external-link" />
                    </a>
                  </div>
                </div>
              </label>
            </FormElement>
          </div>
        </div>
      </section>

      <section>
        <div class="section__headline">
          <SectionHeadline translation-key="step_installation.section_buffer_integration_headline" />
          <p>{{ $t('step_installation.section_buffer_integration_description') }}</p>
        </div>

        <template v-if="projectHasBufferTank">
          <div class="fTable is--products-table has--selects is--add-product-table">
            <div class="fTable__item is--select">
              <FormElement>
                <RadioButton
                  id="decouplingBuffer" v-model="bufferStrategy" :disabled="isDecouplingBufferStrategyDisabled"
                  :value="BufferStrategy.DECOUPLING" name="decouplingBuffer"
                />

                <label class="fTable__tr fTable__cb" for="decouplingBuffer">
                  <div class="fTable__td column is--product">
                    <IconDecouplingBuffer />

                    <div class="fTable__product-infos">
                      <h3 class="product__title">{{ $t('step_installation.decoupling_buffer_title') }}</h3>
                      <p>{{ $t('step_installation.decoupling_buffer_description') }}</p>
                    </div>
                  </div>
                </label>
              </FormElement>
            </div>

            <div class="fTable__item is--select">
              <FormElement>
                <RadioButton
                  id="returnFlowBuffer" v-model="bufferStrategy" :disabled="isReturnFlowBufferStrategyDisabled"
                  :value="BufferStrategy.RETURN_FLOW" name="returnFlowBuffer"
                />

                <label class="fTable__tr fTable__cb" for="returnFlowBuffer">
                  <div class="fTable__td column is--product">
                    <IconReturnFlowBuffer />

                    <div class="fTable__product-infos">
                      <h3 class="product__title">{{ $t('step_installation.return_flow_buffer_title') }}</h3>
                      <p>{{ $t('step_installation.return_flow_buffer_description') }}</p>
                    </div>
                  </div>
                </label>
              </FormElement>
            </div>
          </div>
        </template>

        <ExclusionHint
          v-else
          :hide-card="true"
          :text="$t('step_components.buffer_tank_excluded_description')"
          :title="$t('step_components.buffer_tank_excluded_title')"
        />
      </section>

      <section>
        <div class="section__headline">
          <SectionHeadline translation-key="step_installation.section_mounting_material_headline" />
          <p>{{ $t('step_installation.section_mounting_material_description') }}</p>
        </div>
        <div class="fTable is--products-table has--selects is--add-product-table">
          <MountingMaterialItem
            v-for="material in MountingMaterial"
            :key="material"
            v-model="mountingMaterial"
            :disabled="!availableMountingMaterials.includes(material)"
            :mounting-material="material"
          />
        </div>
      </section>

      <input class="is--invisible" type="submit">
    </form>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

form {
  max-width: unset;

  section {
    padding: rem-calc(32);

    @include media('<tablet') {
      padding: 1rem;
    }

    &:not(:last-of-type) {
      border-bottom: 1px solid $borderColorLight;
    }

    .form__info {
      margin-bottom: 0;
      font-size: 0.875rem;
    }

    .section__headline {
      margin-bottom: 2rem;
    }

    .product__title {
      font-size: 1.25rem;
      margin-bottom: 0;
    }

    .has--selects {
      width: 100%;
      border: 1px solid $borderColor;

      @include themify($themes) {
        border-radius: rem-calc(themed('cardBorderRadius'));
      }
    }

    svg {
      margin-right: 2rem;
      height: 6rem;
      width: 6rem;
    }

    .form__checkbox {
      display: flex;
    }

    .form__element {
      margin-bottom: 0;
      width: 100%;
    }

    p:last-of-type {
      margin-bottom: 0;
    }

    .fTable.is--products-table .fTable__td.is--product {
      padding-top: 1rem;
      padding-bottom: 1rem;
      padding-right: 2rem;
      flex-direction: row;
      align-items: center;
    }

    .fTable__item:has(input:disabled) {
      opacity: 0.3;
      cursor: not-allowed;

      * {
        cursor: not-allowed;
      }

      .form__info {
        color: $textColor;
      }
    }
  }

  .external-link {
    display: inline-block;
    width: auto;
    max-width: max-content;
    text-decoration: none;
    text-align: left;
    font-size: 0.875rem;
  }
}
</style>
